// Copyright (c) Docugami, Inc. All rights reserved.

import { DocSourceId, ExplorerButtonId, ExplorerViewType, NavigationGroup } from "components/myContentExplorer/ExplorerTypes";
import { NavigationListItem } from "components/myContentExplorer/NavigationItemList";
import { PlaceholderText } from "components/myContentExplorer/PlaceholderText";
import { dgIconNames } from "hooks/dgIcons";
import { ProviderId } from "networking/StorageProviders";
import { useMemo } from "react";
import { useGetStorageProvidersQuery } from "store/frontend/frontendSlice";
import { getProviderFriendlyName } from "storage/StorageProviderHelpers";
import { usePageLimits } from "./PageLimits";
import { IExplorerNavigationGroup } from "./ExplorerNavigationGroups";
import { useDocSourceGroupStatusItems } from "./ExplorerStatus";
import { NavigationType, useNavigationService } from "./navigationService";
import { AutomationId, getAutomationIdForDocSourceId } from "utils/Automation";

export function useDocSourceNavigationGroup(): IExplorerNavigationGroup {
    const { data: providers } = useGetStorageProvidersQuery();
    const { pageUploadDisabled } = usePageLimits();
    const navService = useNavigationService();

    const getPlaceholder = (): JSX.Element => {
        const placeholderText = pageUploadDisabled ? "Upgrade now to add some docs" : "No docs added yet.";
        return <PlaceholderText text={placeholderText} onClick={navService.navToAddDocsPage} />
    }

    const statusItems = useDocSourceGroupStatusItems();
    const items = useMemo(() => {
        const items: Array<NavigationListItem> = [
            {
                id: ExplorerButtonId.AddDocs,
                title: 'Add Docs',
                type: ExplorerViewType.AddDocs,
                icon: {
                    name: dgIconNames.DgPlusBox,
                    height: 12,
                    width: 12
                },
                hasDivider: true,
                disabled: pageUploadDisabled,
                onClick: navService.navToAddDocsPage,
                automationId: AutomationId.DocExplorerAddDocs
            },
            {
                id: DocSourceId.Docugami,
                title: "Local Docs",
                type: ExplorerViewType.LocalDocSource,
                icon: {
                    name: dgIconNames.DgComputerDocs,
                    height: 11.67,
                    width: 13
                },
                onClick: () => navService.navToLocalDocs(NavigationType.PushNewPage),
                automationId: AutomationId.DocExplorerSourceLocalDocs
            }
        ];

        if (!!providers) {
            items.push(...providers.providers.map(x => getItemForProviderId(x, (id) => navService.navToRemoteDocs(NavigationType.PushNewPage, id))));
        }

        return items;
    }, [providers, pageUploadDisabled, navService]);

    return {
        id: NavigationGroup.DocSources,
        title: "Docs",
        items: items,
        collapsedItemCount: Infinity,
        placeholder: getPlaceholder(),
        statusItems: statusItems
    };
}

function getItemForProviderId(providerId: ProviderId, onSelectItem: (id: DocSourceId) => void): NavigationListItem {
    switch (providerId) {
        case ProviderId.OneDrive:
            return {
                id: DocSourceId.OneDrive,
                title: getProviderFriendlyName(providerId),
                type: ExplorerViewType.RemoteDocSource,
                icon: {
                    name: dgIconNames.DgOneDrive,
                    height: 13,
                    width: 13
                },
                onClick: () => onSelectItem(DocSourceId.OneDrive),
                automationId: getAutomationIdForDocSourceId(DocSourceId.OneDrive)
            };

        case ProviderId.SharePoint:
            return {
                id: DocSourceId.SharePoint,
                title: getProviderFriendlyName(providerId),
                type: ExplorerViewType.RemoteDocSource,
                icon: {
                    name: dgIconNames.DgSharePoint,
                    height: 13,
                    width: 13
                },
                onClick: () => onSelectItem(DocSourceId.SharePoint),
                automationId: getAutomationIdForDocSourceId(DocSourceId.SharePoint)
            };

        case ProviderId.Box:
            return {
                id: DocSourceId.Box,
                title: getProviderFriendlyName(providerId),
                type: ExplorerViewType.RemoteDocSource,
                icon: {
                    name: dgIconNames.DgBoxLogo,
                    height: 13,
                    width: 13
                },
                onClick: () => onSelectItem(DocSourceId.Box),
                automationId: getAutomationIdForDocSourceId(DocSourceId.Box)
            };

        case ProviderId.Dropbox:
            return {
                id: DocSourceId.Dropbox,
                title: getProviderFriendlyName(providerId),
                type: ExplorerViewType.RemoteDocSource,
                icon: {
                    name: dgIconNames.DgDropboxLogo,
                    height: 13,
                    width: 13
                },
                onClick: () => onSelectItem(DocSourceId.Dropbox),
                automationId: getAutomationIdForDocSourceId(DocSourceId.Dropbox)
            };

        default:
            throw new Error('Unmapped provider ID');
    }
}
